import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { supabase } from '../../lib/supabase';
import { readEdgeFunctionError } from '../../lib/edgeError';

// ─── Error Helpers ────────────────────────────────────────────────────────────

/**
 * Categoriza erro de Edge Function e retorna mensagem em português ≤ 200 chars.
 * Satisfaz Requirement 9.1: mensagem descreve categoria de falha.
 */
function categorizeEdgeFunctionError(
  error: { message?: string } | null | undefined,
  fallback: string
): string {
  const msg = error?.message?.toLowerCase() || '';

  let result: string;
  if (msg.includes('timeout') || msg.includes('timed out') || msg.includes('aborted')) {
    result = 'Falha na comunicação com serviço de pagamento. Tempo limite excedido.';
  } else if (
    msg.includes('network') ||
    msg.includes('fetch') ||
    msg.includes('failed to') ||
    msg.includes('connection') ||
    msg.includes('econnrefused')
  ) {
    result = 'Erro de conexão. Verifique sua internet e tente novamente.';
  } else if (msg.includes('500') || msg.includes('502') || msg.includes('503') || msg.includes('server')) {
    result = 'Erro no servidor de pagamento. Tente novamente em alguns instantes.';
  } else if (msg.includes('non-2xx')) {
    // O texto genérico do supabase-js ("Edge Function returned a non-2xx status code"), em
    // inglês e sem dizer nada: a tela mostrava isso (QA do pagamento, 04/10/2026). O motivo real
    // vem no corpo; quem pode lê-lo usa `readEdgeFunctionError`.
    result = fallback;
  } else if (msg && msg.length <= 200) {
    // Use the original message if it's short enough (may come from Edge Function in Portuguese)
    result = error!.message!;
  } else if (msg) {
    // Original message is too long — truncate it
    result = error!.message!;
  } else {
    result = fallback;
  }

  // Enforce 200 character limit
  return result.length > 200 ? result.slice(0, 197) + '...' : result;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PixData {
  qrCode: string | null;
  copyPaste: string | null;
  expiresAt: string | null;
}

export type BillingCycle = 'MONTHLY' | 'YEARLY';

// Configuração de preços do plano, lida do Supabase (asaas_plan_config). Editável sem deploy.
export interface PlanConfig {
  name: string;
  monthlyValue: number;
  annualValue: number | null;
  annualEnabled: boolean;
  // Valor do pagamento único (desbloqueio de perfil). Mesma config, editável sem deploy.
  profileUnlockValue: number | null;
  // Pix Automático (débito recorrente) ligado no checkout. Desligado por padrão: o fluxo por
  // cobrança (QR por ciclo) continua sendo o caminho, e segue existindo como fallback mesmo
  // depois — nem todo banco do pagador suporta Pix Automático.
  pixAutomaticEnabled: boolean;
  // Cotas inclusas e preço dos add-ons da assinatura única.
  includedProfiles: number;
  profileAddonValue: number;
  includedStorageGb: number;
  storageAddonValuePerGb: number;
}

export interface SubscriptionState {
  status: 'active' | 'overdue' | 'cancelled' | 'pending' | 'none';
  asaasCustomerId: string | null;
  asaasSubscriptionId: string | null;
  nextDueDate: string | null;
  value: number | null;
  /**
   * O ciclo CONTRATADO (`MONTHLY` | `YEARLY`), lido de `asaas_subscriptions.cycle`.
   *
   * ⚠️ Sem ele as Configurações rotulavam o valor como "Valor mensal" sempre, e quem assinou
   * anual lia "Valor mensal: R$ 463,20". O dado já era gravado no checkout; faltava trazê-lo de
   * volta. `null` quando não há assinatura, ou quando a edge ainda está numa versão que não
   * devolve o campo.
   */
  cycle: 'MONTHLY' | 'YEARLY' | null;
  /**
   * Como a assinatura é cobrada: `CREDIT_CARD`, `PIX` ou `PIX_AUTOMATIC`.
   *
   * ⚠️ Serve para a tela de ajustar plano avisar ANTES. A autorização do Pix Automático foi dada
   * para um valor e não carrega as quantidades, então o servidor recusa o ajuste — e descobrir
   * isso depois de escolher tudo é a pior hora de descobrir.
   */
  billingType: 'CREDIT_CARD' | 'PIX' | 'PIX_AUTOMATIC' | null;
  /**
   * O plano desta pessoa só muda pelo suporte.
   *
   * ⚠️ Só é `true` nas autorizações de Pix Automático ANTIGAS, criadas com `value` travado — a
   * Asaas não deixa alterar o valor das instruções seguintes, e não existe endpoint para mudar a
   * autorização. As novas nascem sem `value` e são ajustáveis pela própria conta.
   */
  planoTravadoNoBanco: boolean;
  /**
   * Percentual do cupom desta assinatura, 0 quando não há.
   *
   * ⚠️ A tela de ajuste PRECISA dele. Sem ele ela previa o preço de tabela enquanto o servidor
   * cobrava com desconto — e, antes do conserto, o próprio servidor perdia o cupom no ajuste.
   */
  discountPercent: number;
  /**
   * O código do cupom, para a tela poder NOMEAR o desconto.
   *
   * Só o percentual não basta: um total menor que o de tabela, sem nada explicando por quê, é
   * indistinguível de um erro de cálculo. Foi essa ambiguidade que deixou um desconto de 90%
   * ser destruído sem ninguém notar — R$ 57,90 parecia certo porque era o preço de tabela.
   */
  couponCode: string | null;
  /** Quantidades já AGENDADAS para a virada do ciclo (redução). `null` quando não há nenhuma. */
  scheduledExtraProfiles: number | null;
  scheduledExtraStorageGb: number | null;
  gracePeriodEndsAt: string | null;
  /**
   * CANCELADA, com o PRO até o fim do período pago (QA de pagamentos, C3; cláusula 8 dos Termos):
   * o status segue `active` até esta data, e as cobranças já pararam. `null` quando não há
   * cancelamento marcado.
   */
  acessoAte: string | null;
  /** Quando a redução agendada (`scheduledExtra*`) passa a valer: o fim do período já pago (M10). */
  reducaoValeEm: string | null;
  /** O valor do período em curso enquanto há redução agendada (`value` já é o da próxima cobrança). */
  valorAteAReducao: number | null;
  /** A diferença proporcional de subidas ainda não paga: vai na próxima cobrança (A6). */
  proporcionalPendente: number;
  /**
   * O preço DESTA assinatura (mensal e anual, sem add-ons), a base dos ajustes enquanto ela
   * existir (QA de pagamentos, A3). `null` quando não há: a tela usa o de tabela. Sem ele, a tela
   * de ajuste previa pela tabela e o servidor cobrava por este.
   */
  precoBaseMensal: number | null;
  precoBaseAnual: number | null;
  // Assinatura ativa COM a cobranca do ciclo novo em aberto. A assinatura PIX da Asaas e
  // recorrencia de COBRANCA, nao de debito: a cada ciclo nasce um QR a pagar. Sem este sinal
  // a tela mostrava "Ativa" e nao avisava nada.
  pendingRenewal: boolean;
  // O `pixData` em tela é de uma AUTORIZAÇÃO de Pix Automático, não de uma cobrança avulsa.
  // Pagar aquele QR autoriza os débitos dos próximos ciclos no banco do assinante, e a tela
  // precisa dizer isso ANTES do pagamento. Vive no estado porque chega por dois caminhos: o
  // checkout novo (`createPixAuthorization`) e a retomada de quem está migrando (`resumePayment`).
  pixAutomatic: boolean;
  plan: PlanConfig | null;
  // Cotas da conta, como o servidor as reporta. A regra de quem tem direito a quanto mora em SQL
  // (`public.storage_quota_bytes`), que é a mesma função que recusa o envio no gatilho do
  // Storage — aqui só guardamos o número para a tela mostrar.
  includedProfiles: number;
  extraProfiles: number;
  extraStorageGb: number;
  storageQuotaBytes: number;
  storageUsedBytes: number;
  /** Perfis do usuário que disputam a cota; os legados ficam de fora por terem sido comprados. */
  profilesInQuota: number;
  legacyProfiles: number;
  loading: boolean;
  error: string | null;
  pixData: PixData | null;
  // false até a primeira resposta do servidor. `status: 'none'` só é confiável
  // depois disso — antes é apenas o default do Redux (evita banner falso).
  initialized: boolean;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: SubscriptionState = {
  status: 'none',
  asaasCustomerId: null,
  asaasSubscriptionId: null,
  nextDueDate: null,
  value: null,
  cycle: null,
  billingType: null,
  planoTravadoNoBanco: false,
  discountPercent: 0,
  couponCode: null,
  scheduledExtraProfiles: null,
  scheduledExtraStorageGb: null,
  gracePeriodEndsAt: null,
  acessoAte: null,
  reducaoValeEm: null,
  valorAteAReducao: null,
  proporcionalPendente: 0,
  precoBaseMensal: null,
  precoBaseAnual: null,
  pendingRenewal: false,
  pixAutomatic: false,
  plan: null,
  includedProfiles: 0,
  extraProfiles: 0,
  extraStorageGb: 0,
  storageQuotaBytes: 0,
  storageUsedBytes: 0,
  profilesInQuota: 0,
  legacyProfiles: 0,
  loading: false,
  error: null,
  pixData: null,
  initialized: false,
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

/**
 * Consulta o status atual da assinatura do usuário.
 * Chama a Edge Function `asaas-subscription-status`.
 */
export const fetchSubscriptionStatus = createAsyncThunk(
  'subscription/fetchStatus',
  async (_, { rejectWithValue }) => {
    const { data, error } = await supabase.functions.invoke('asaas-subscription-status');

    if (error) {
      return rejectWithValue(
        categorizeEdgeFunctionError(error, 'Erro ao consultar status da assinatura')
      );
    }

    // As cotas vêm de uma RPC à parte, e não da edge de pagamento: a regra de quanto espaço cada
    // um tem mora em SQL (é a mesma função que o gatilho do Storage usa para recusar o envio), e
    // a função não aceita parâmetro — quem é o usuário sai de `auth.uid()`.
    //
    // Falha aqui NÃO derruba a consulta: saber se a assinatura está ativa é o que decide acesso;
    // a cota é informação de tela. Perder o número não pode trancar ninguém para fora.
    const { data: cotas } = await supabase.rpc('account_quotas').single();

    return {
      ...(data as Record<string, unknown>),
      includedProfiles: (cotas as any)?.included_profiles,
      extraProfiles: (cotas as any)?.extra_profiles,
      extraStorageGb: (cotas as any)?.extra_storage_gb,
      storageQuotaBytes: (cotas as any)?.storage_quota_bytes,
      storageUsedBytes: (cotas as any)?.storage_used_bytes,
      profilesInQuota: (cotas as any)?.profiles_in_quota,
      legacyProfiles: (cotas as any)?.legacy_profiles,
    } as {
      status: SubscriptionState['status'];
      asaasCustomerId: string | null;
      asaasSubscriptionId: string | null;
      nextDueDate: string | null;
      value: number | null;
      /** O ciclo CONTRATADO, para a tela não chamar de mensal o que se paga uma vez por ano. */
      cycle?: string | null;
      billingType?: string | null;
      planoTravadoNoBanco?: boolean;
      discountPercent?: number;
      couponCode?: string | null;
      scheduledExtraProfiles?: number | null;
      scheduledExtraStorageGb?: number | null;
      gracePeriodEndsAt: string | null;
      pendingRenewal?: boolean;
      acessoAte?: string | null;
      reducaoValeEm?: string | null;
      valorAteAReducao?: number | null;
      proporcionalPendente?: number;
      precoBaseMensal?: number | null;
      precoBaseAnual?: number | null;
      // Opcionais porque a RPC de cotas pode falhar sem derrubar a consulta de status.
      includedProfiles?: number;
      extraProfiles?: number;
      extraStorageGb?: number;
      storageQuotaBytes?: number;
      storageUsedBytes?: number;
      /** Perfis que disputam a cota (o legado fica de fora). */
      profilesInQuota?: number;
      legacyProfiles?: number;
    };
  }
);

/**
 * Carrega a configuração de preços do plano ativo (asaas_plan_config) direto do Supabase.
 * RLS: policy "Anyone can read plan config" permite leitura por authenticated/anon.
 * Mantém os preços exibidos em sincronia com o que a edge cobra (editável sem deploy).
 */
export const fetchPlanConfig = createAsyncThunk(
  'subscription/fetchPlanConfig',
  async (_, { rejectWithValue }) => {
    const { data, error } = await supabase
      .from('asaas_plan_config')
      // ⚠️ String LITERAL, numa linha só: o supabase-js infere as colunas do texto do `select` em
      // tempo de tipo. Quebrar isto em concatenação faz a inferência cair para
      // `GenericStringError` e o build inteiro para de compilar.
      .select('name, monthly_value, annual_value, annual_enabled, profile_unlock_value, pix_automatic_enabled, included_profiles, profile_addon_value, included_storage_gb, storage_addon_value_per_gb')
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      return rejectWithValue('Erro ao carregar a configuração do plano');
    }

    return {
      name: (data.name as string) ?? 'Maestra PRO',
      monthlyValue: Number(data.monthly_value),
      annualValue: data.annual_value != null ? Number(data.annual_value) : null,
      annualEnabled: !!data.annual_enabled,
      profileUnlockValue: data.profile_unlock_value != null ? Number(data.profile_unlock_value) : null,
      pixAutomaticEnabled: !!data.pix_automatic_enabled,
      includedProfiles: Number(data.included_profiles ?? 1),
      profileAddonValue: Number(data.profile_addon_value ?? 27.9),
      includedStorageGb: Number(data.included_storage_gb ?? 30),
      storageAddonValuePerGb: Number(data.storage_addon_value_per_gb ?? 7.9),
    } as PlanConfig;
  }
);

/**
 * Cria um cliente no Asaas com os dados fornecidos.
 * Chama a Edge Function `asaas-create-customer`.
 */
export const createAsaasCustomer = createAsyncThunk(
  'subscription/createCustomer',
  async (
    // `mobilePhone` é opcional e só entra quando o usuário preencheu: habilita a régua de
    // SMS/WhatsApp da Asaas, que sem ele fica restrita ao e-mail.
    payload: { name: string; email: string; cpfCnpj: string; mobilePhone?: string },
    { rejectWithValue }
  ) => {
    const { data, error } = await supabase.functions.invoke('asaas-create-customer', {
      body: payload,
    });

    if (error) {
      return rejectWithValue(
        await readEdgeFunctionError(error, 'Erro ao criar cliente no Asaas')
      );
    }

    return data as { customerId: string };
  }
);

/**
 * Cria uma assinatura recorrente no Asaas.
 * Suporta PIX e Cartão de Crédito.
 * Chama a Edge Function `asaas-create-subscription`.
 */
export const createSubscription = createAsyncThunk(
  'subscription/createSubscription',
  async (payload: {
    customerId: string;
    billingType?: 'PIX' | 'CREDIT_CARD';
    cycle?: BillingCycle;
    couponCode?: string;
    /** Add-ons: perfis além do incluso e GB além da cota. A edge recalcula o preço e nunca
     *  confia no valor exibido — daqui vai só a QUANTIDADE. */
    extraProfiles?: number;
    extraStorageGb?: number;
    creditCard?: {
      holderName: string;
      number: string;
      expiryMonth: string;
      expiryYear: string;
      ccv: string;
    };
    creditCardHolderInfo?: {
      name: string;
      email?: string;
      cpfCnpj: string;
      postalCode: string;
      addressNumber?: string;
      address?: string;
      province?: string;
      city?: string;
      uf?: string;
      phone: string;
    };
  }, { rejectWithValue }) => {
    const { data, error } = await supabase.functions.invoke('asaas-create-subscription', {
      body: payload,
    });

    if (error) {
      return rejectWithValue(
        await readEdgeFunctionError(error, 'Erro ao criar assinatura')
      );
    }

    return data as {
      subscriptionId?: string;
      status?: string;
      pixData?: {
        qrCode: string | null;
        copyPaste: string | null;
        expiresAt: string | null;
      } | null;
      // Trava anti-duplicidade da edge: já existe assinatura → não criou outra.
      resume?: boolean;
      alreadyActive?: boolean;
    };
  }
);

/**
 * Retoma um pagamento PIX pendente: busca no Asaas o QR da cobrança em aberto da assinatura
 * existente (sem criar uma nova). Usado ao voltar pra /pagamento depois de fechar a tela.
 * Chama a Edge Function `asaas-resume-payment`.
 */
export const resumePayment = createAsyncThunk(
  'subscription/resumePayment',
  async (_, { rejectWithValue }) => {
    const { data, error } = await supabase.functions.invoke('asaas-resume-payment', { body: {} });

    if (error) {
      return rejectWithValue(
        await readEdgeFunctionError(error, 'Não foi possível abrir o seu pagamento agora. Tente de novo.')
      );
    }

    return data as {
      status: SubscriptionState['status'];
      // 'CREDIT_CARD' quando a assinatura pendente é de cartão (em análise, sem QR).
      billingType?: string | null;
      // true quando a cobrança em aberto é de RENOVAÇÃO (a assinatura já teve ciclo pago).
      // Sem isto a tela não distingue "assinatura em dia" de "assinatura ativa com o ciclo
      // novo em aberto", e mostra sucesso para quem ainda precisa pagar.
      pendingRenewal?: boolean;
      // true quando o QR devolvido é de uma AUTORIZAÇÃO de Pix Automático, não de uma cobrança
      // avulsa. A tela precisa saber: pagar este QR autoriza débitos futuros, e dizer isso é
      // obrigação nossa, não detalhe de implementação.
      pixAutomatic?: boolean;
      // true quando este é um assinante ANTIGO sendo migrado nesta renovação.
      migrating?: boolean;
      dueDate?: string | null;
      pixData?: PixData | null;
      value?: number | null;
      cycle?: string | null;
      // A FATURA da Asaas da mensalidade em atraso, no cartão ou no Pix Automático (04/10/2026):
      // ali não há QR nosso, e a cobrança vencida se paga pela página segura da Asaas.
      invoiceUrl?: string | null;
    };
  }
);

/**
 * Cria uma autorização de Pix Automático (débito recorrente).
 * Chama a Edge Function `asaas-create-pix-authorization`.
 *
 * A resposta traz `pixData` com os MESMOS campos do fluxo por cobrança — a tela de pagamento não
 * precisa de forma nova. A diferença é o significado: pagar este QR não quita só um ciclo, ele
 * autoriza os débitos seguintes (Jornada 3).
 */
export const createPixAuthorization = createAsyncThunk(
  'subscription/createPixAuthorization',
  async (
    payload: {
      customerId: string;
      cycle?: BillingCycle;
      couponCode?: string;
      /** Add-ons: a autorização passou a carregá-los, então o QR já sai no valor contratado. */
      extraProfiles?: number;
      extraStorageGb?: number;
    },
    { rejectWithValue }
  ) => {
    const { data, error } = await supabase.functions.invoke('asaas-create-pix-authorization', {
      body: payload,
    });

    if (error) {
      return rejectWithValue(
        await readEdgeFunctionError(error, 'Erro ao criar a autorização de pagamento')
      );
    }

    return data as {
      authorizationId?: string;
      authorizationStatus?: string;
      status?: string;
      pixData?: PixData | null;
      value?: number | null;
      cycle?: string | null;
      alreadyActive?: boolean;
      resume?: boolean;
    };
  }
);

/**
 * Cancela a assinatura do usuário no Asaas.
 * Chama a Edge Function `asaas-cancel-subscription`.
 */
/**
 * Ajusta as quantidades de add-on de uma assinatura ATIVA.
 *
 * ⚠️ A RESPOSTA DIZ QUANDO A MUDANÇA VALE, e as duas coisas podem ser verdade na mesma ação:
 * aumentar libera na hora (`aplicadoAgora`), reduzir espera a virada do ciclo já pago
 * (`agendadoParaProximoCiclo`). Quem chama precisa mostrar isso — dizer só "pronto" faria a
 * pessoa achar que perdeu acesso que ainda tem, ou que ganhou espaço que ainda não veio.
 *
 * Depois de resolver, redespache `fetchSubscriptionStatus`: a cota nova sai da RPC, não daqui.
 */
export const atualizarAddons = createAsyncThunk(
  'subscription/atualizarAddons',
  async (
    payload: {
      extraProfiles: number;
      extraStorageGb: number;
      /**
       * OMITIDO mantém o cupom que já vale; string com código troca por outro (validado como no
       * checkout); string vazia remove o desconto. O campo ausente e o campo vazio querem dizer
       * coisas opostas, e confundi-los apagaria o desconto de quem só mexeu nas quantidades.
       */
      couponCode?: string | null;
      /**
       * Os perfis travados a liberar com os perfis a mais (C5): a escolha vai junto e o servidor
       * a grava. Sem ela, o banco libera os mais antigos.
       */
      liberar?: string[];
      /** Os perfis ativos que ficam numa redução abaixo dos perfis da conta (M9). */
      manter?: string[];
    },
    { rejectWithValue },
  ) => {
    const { data, error } = await supabase.functions.invoke('asaas-update-subscription', {
      body: payload,
    });

    if (error) {
      // `readEdgeFunctionError` e não `categorize`: as recusas desta function trazem a RAZÃO
      // ("exclua um perfil antes", "libere arquivos"), e é ela que resolve o problema da pessoa.
      return rejectWithValue(await readEdgeFunctionError(error, 'Erro ao ajustar o plano'));
    }

    return data as {
      success: boolean;
      value: number;
      cycle: 'MONTHLY' | 'YEARLY';
      aplicadoAgora: boolean;
      agendadoParaProximoCiclo: boolean;
      /** Voltou ao que já está em vigor: cancelou uma redução agendada, sem aumento nem redução. */
      desfezAgendamento: boolean;
      /** O cupom mudou nesta alteração. */
      trocouCupom: boolean;
      /** O cupom que passou a valer, `null` quando não há nenhum. */
      couponCode: string | null;
      proximaCobranca: string | null;
      extraProfiles: number;
      extraStorageGb: number;
      /** null: nada a liberar; false: a escolha não foi gravada (o banco liberou pela ordem). */
      perfisLiberados?: boolean | null;
      /** A diferença proporcional dos dias que faltam (A6), e se ela entrou na próxima cobrança. */
      proporcional?: number;
      proporcionalNaCobranca?: boolean | null;
      /** Quando a redução vale (M10). */
      reducaoValeEm?: string | null;
    };
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (_, { rejectWithValue }) => {
    const { data, error } = await supabase.functions.invoke('asaas-cancel-subscription', {
      body: {},
    });

    if (error) {
      return rejectWithValue(
        await readEdgeFunctionError(error, 'Não foi possível cancelar a assinatura agora. Tente de novo.')
      );
    }

    // `acessoAte`: até quando o PRO segue (o fim do período pago); null, encerrada agora.
    return data as { success: boolean; acessoAte?: string | null };
  }
);

/**
 * Descarta a assinatura PENDENTE (gerada e nunca paga), para trocar o plano ou a forma de
 * pagamento, ou desistir (04/10/2026). O servidor confere na Asaas antes: se o pagamento já caiu,
 * nada se descarta (`jaPago`), e a tela leva à confirmação. Descartada, o QR antigo deixa de valer
 * e a conta volta a "sem assinatura" (não "cancelada": nada foi cobrado).
 */
export const descartarAssinaturaPendente = createAsyncThunk(
  'subscription/descartarPendente',
  async (_, { rejectWithValue }) => {
    const { data, error } = await supabase.functions.invoke('asaas-cancel-subscription', {
      body: { modo: 'pendente' },
    });
    if (error) {
      return rejectWithValue(await readEdgeFunctionError(error, 'Não foi possível alterar a assinatura agora. Tente de novo em instantes.'));
    }
    return data as { descartada?: boolean; jaPago?: boolean; naoPendente?: boolean; emAnalise?: boolean };
  }
);

/**
 * Polling do status de pagamento.
 * Verifica a cada 5 segundos por até 10 minutos se o pagamento foi confirmado.
 * Resolve quando status === 'active' ou rejeita por timeout.
 *
 * Resiliência a erros de rede:
 * - 1 erro: skip iteração, continua no próximo intervalo sem resetar timer
 * - 3 erros consecutivos: rejeita com mensagem de conectividade
 * - Qualquer resposta bem-sucedida reseta o contador de erros
 */
export const pollPaymentStatus = createAsyncThunk(
  'subscription/pollPaymentStatus',
  // `signal`: quem chamou pode parar a espera (a tela que saiu de cena). Antes ela seguia viva por
  // 10 minutos e, no cartão, ainda navegava sozinha depois (QA do pagamento, 04/10/2026).
  async (_, { rejectWithValue, signal }) => {
    const POLL_INTERVAL_MS = 5000;
    const TIMEOUT_MS = 10 * 60 * 1000; // 10 minutos
    const MAX_CONSECUTIVE_ERRORS = 3;

    const startTime = Date.now();
    let consecutiveErrors = 0;

    while (Date.now() - startTime < TIMEOUT_MS) {
      if (signal.aborted) return rejectWithValue('cancelado');
      const { data, error } = await supabase.functions.invoke(
        'asaas-subscription-status'
      );

      if (error) {
        consecutiveErrors++;

        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          return rejectWithValue(
            'Conexão perdida. Verifique sua internet e tente novamente.'
          );
        }

        // Skip iteração, continua no próximo intervalo sem resetar timer
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        continue;
      }

      // Resposta bem-sucedida: reseta contador de erros consecutivos
      consecutiveErrors = 0;

      // A assinatura pendente deixou de existir (o servidor a descartou: o PIX venceu, ou foi
      // alterada em outra aba). Esperar mais não tem o que confirmar.
      if (data?.status === 'none') return rejectWithValue('sem_assinatura');

      // `active` sozinho NÃO significa pago. Numa RENOVAÇÃO a assinatura continua ativa enquanto
      // a cobrança do ciclo novo está em aberto — o poll resolvia na primeira volta, a tela
      // declarava "pagamento confirmado" e mandava pro sucesso sem ninguém ter pago nada.
      // Só encerra quando a renovação em aberto também sumiu (webhook/cron limparam o vínculo).
      if (data?.status === 'active' && !data?.pendingRenewal) {
        return data as {
          status: SubscriptionState['status'];
          asaasCustomerId: string | null;
          asaasSubscriptionId: string | null;
          nextDueDate: string | null;
          value: number | null;
          gracePeriodEndsAt: string | null;
          pendingRenewal?: boolean;
        };
      }

      // Aguarda 5 segundos antes do próximo polling
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    return rejectWithValue(
      'O pagamento não foi confirmado no tempo limite. Verifique novamente mais tarde.'
    );
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    resetSubscription() {
      return initialState;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchSubscriptionStatus
    builder
      .addCase(fetchSubscriptionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.status = action.payload.status;
        state.asaasCustomerId = action.payload.asaasCustomerId;
        state.asaasSubscriptionId = action.payload.asaasSubscriptionId;
        state.nextDueDate = action.payload.nextDueDate;
        state.value = action.payload.value;
        // `YEARLY` quando a assinatura é anual. A edge pode estar numa versão anterior e não
        // mandar o campo; `?? null` deixa a tela no rótulo neutro em vez de mentir.
        state.cycle = (action.payload.cycle as SubscriptionState['cycle']) ?? null;
        state.billingType = (action.payload.billingType as SubscriptionState['billingType']) ?? null;
        state.planoTravadoNoBanco = !!action.payload.planoTravadoNoBanco;
        state.discountPercent = Number(action.payload.discountPercent) || 0;
        // A edge pode estar numa versão anterior e não mandar o código. A tela então diz só
        // "Desconto", que é verdade, em vez de inventar um nome.
        state.couponCode = action.payload.couponCode ?? null;
        state.scheduledExtraProfiles = action.payload.scheduledExtraProfiles ?? null;
        state.scheduledExtraStorageGb = action.payload.scheduledExtraStorageGb ?? null;
        state.gracePeriodEndsAt = action.payload.gracePeriodEndsAt;
        state.pendingRenewal = !!action.payload.pendingRenewal;
        state.acessoAte = action.payload.acessoAte ?? null;
        state.reducaoValeEm = action.payload.reducaoValeEm ?? null;
        state.valorAteAReducao = action.payload.valorAteAReducao ?? null;
        state.proporcionalPendente = Number(action.payload.proporcionalPendente) || 0;
        // Uma edge anterior não manda o preço: a tela volta ao de tabela, como era.
        state.precoBaseMensal = action.payload.precoBaseMensal ?? null;
        state.precoBaseAnual = action.payload.precoBaseAnual ?? null;
        // Se a RPC de cotas falhou, mantém o que já havia: sem o `??` a cota viraria `undefined`
        // e a tela mostraria "0 GB" para quem tem espaço de sobra.
        state.includedProfiles = action.payload.includedProfiles ?? state.includedProfiles;
        state.extraProfiles = action.payload.extraProfiles ?? state.extraProfiles;
        state.extraStorageGb = action.payload.extraStorageGb ?? state.extraStorageGb;
        state.storageQuotaBytes = action.payload.storageQuotaBytes ?? state.storageQuotaBytes;
        state.storageUsedBytes = action.payload.storageUsedBytes ?? state.storageUsedBytes;
        state.profilesInQuota = action.payload.profilesInQuota ?? state.profilesInQuota;
        state.legacyProfiles = action.payload.legacyProfiles ?? state.legacyProfiles;
      })
      .addCase(fetchSubscriptionStatus.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = (action.payload as string) || 'Erro ao consultar assinatura';
      });

    // fetchPlanConfig
    builder
      .addCase(fetchPlanConfig.fulfilled, (state, action) => {
        state.plan = action.payload;
      });

    // resumePayment
    builder
      .addCase(resumePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resumePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload.status;
        if (action.payload.value != null) state.value = action.payload.value;
        state.pixData = action.payload.pixData ?? null;
      })
      .addCase(resumePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Erro ao retomar o pagamento';
      });

    // createAsaasCustomer
    builder
      .addCase(createAsaasCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAsaasCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.asaasCustomerId = action.payload.customerId;
      })
      .addCase(createAsaasCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Erro ao criar cliente';
      });

    // createSubscription
    builder
      .addCase(createSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Pix Automático: o QR da autorização entra no mesmo `pixData` do fluxo por cobrança, para
      // a tela de pagamento não precisar de um caminho paralelo.
      .addCase(createPixAuthorization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPixAuthorization.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Erro ao criar a autorização de pagamento';
      })
      .addCase(createPixAuthorization.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.alreadyActive) {
          state.status = 'active';
          state.pixData = null;
          return;
        }
        if (action.payload.resume) return;
        state.status = 'pending';
        state.pixData = action.payload.pixData ?? null;
        // Sem isto o aviso de débito recorrente só aparecia para quem estava MIGRANDO, e não
        // para quem contrata pelo checkout — que é a maioria e a via principal.
        state.pixAutomatic = true;
        if (action.payload.value != null) state.value = action.payload.value;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.loading = false;

        // Trava anti-duplicidade (edge): já existe assinatura.
        if (action.payload.alreadyActive) {
          state.status = 'active';
          state.pixData = null;
          return;
        }
        if (action.payload.resume) {
          // Mantém o status atual (pending); a página navega pra /pagamento (retomar).
          return;
        }

        state.asaasSubscriptionId = action.payload.subscriptionId ?? state.asaasSubscriptionId;

        // Credit card → active immediately
        if (action.payload.status === 'active') {
          state.status = 'active';
          state.pixData = null;
          return;
        }

        // PIX flow — guard against null/absent qrCode
        const responsePixData = action.payload.pixData;
        if (!responsePixData?.qrCode) {
          state.error =
            'Não foi possível gerar o QR Code PIX. Tente novamente.';
          state.pixData = null;
          return;
        }

        // PIX flow — happy path
        state.status = 'pending';
        state.pixData = {
          qrCode: responsePixData.qrCode,
          copyPaste: responsePixData.copyPaste,
          expiresAt: responsePixData.expiresAt,
        };
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Erro ao criar assinatura';
      });

    // cancelSubscription
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = false;
        // Com o período pago à frente, a assinatura segue ativa até ele acabar (C3).
        if (action.payload?.acessoAte) {
          state.acessoAte = action.payload.acessoAte;
          state.pendingRenewal = false;
        } else {
          state.status = 'cancelled';
          state.acessoAte = null;
        }
        state.pixData = null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Erro ao cancelar assinatura';
      })
      // Descartada, a conta volta a "sem assinatura" na hora (a próxima consulta confirma). O erro
      // fica com quem chamou: cada tela diz o seu.
      .addCase(descartarAssinaturaPendente.fulfilled, (state, action) => {
        if (!action.payload?.descartada) return;
        state.status = 'none';
        state.pixData = null;
        state.asaasSubscriptionId = null;
        state.billingType = null;
        state.value = null;
        state.pixAutomatic = false;
        state.extraProfiles = 0;
        state.extraStorageGb = 0;
      });

    // pollPaymentStatus
    builder
      .addCase(pollPaymentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(pollPaymentStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload.status;
        state.asaasCustomerId = action.payload.asaasCustomerId;
        state.asaasSubscriptionId = action.payload.asaasSubscriptionId;
        state.nextDueDate = action.payload.nextDueDate;
        state.value = action.payload.value;
        state.gracePeriodEndsAt = action.payload.gracePeriodEndsAt;
        state.pendingRenewal = !!action.payload.pendingRenewal;
        state.pixData = null;
      })
      .addCase(pollPaymentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Timeout na verificação do pagamento';
      });
  },
});

export const { resetSubscription, clearError } = subscriptionSlice.actions;

export { categorizeEdgeFunctionError };

export default subscriptionSlice.reducer;
